import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Col,
  Row,
  Form,
  Pagination as BsPagination,
  Button,
} from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
// import { TbPinned } from "react-icons/tb";

import { getLocationOrgs } from "../../../../services/setting/locations/location-list";
import GoogleMultiple from "../../../../components/GoogleMaps/GoogleMapMultiple";
import AutocompleteCustomA from "../../../../components/custom/autocompletecustom";
import InputSearch from "../../../../components/custom/InputSearch";
import Loading from "../../../../components/loading";
import {
  getLocationClearButtonStyle,
  // getLocationButtonStyle,
  buttonTextStyle,
} from "../style/locationStyles";
import { formatNumber } from "../func/display";
import { Pagination } from "antd";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGINATION = { page: 1, limit: 10, total: 0 };
// const LAT_REGEX = /^-?(90(\.0+)?|[0-8]?\d(\.\d+)?)$/;
// const LNG_REGEX = /^-?(180(\.0+)?|1[0-7]\d(\.\d+)?|[0-9]{1,2}(\.\d+)?)$/;

const parseLocationItem = (item) => {
  const lat = Number(item.office_location_lat);
  const lng = Number(item.office_location_lon);
  const radius = Number(item.office_location_radius);

  if (isNaN(lat) || isNaN(lng)) return null;

  return {
    lat,
    lng,
    radius: isNaN(radius) ? 0 : radius,
    name: item.office_location_name,
    nameEn: item.office_location_name_en,
    nameTh: item.office_location_name_th,
    status: item.office_location_status,
    assignTo: item.assignTo,
  };
};

const useLocationData = ({
  orgId,
  search,
  pagination,
  coords,
  selectedStatus,
}) => {
  const [locations, setLocations] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const offset = (pagination.page - 1) * pagination.limit;
  // const hasCoords = Boolean(coords?.lat && coords?.lng && coords?.radius);

  const fetch = useCallback(async () => {
    // if (!hasCoords) return;

    setIsLoading(true);
    try {
      const hasOrgFilter = Boolean(orgId);
      const hasSearchFilter = Boolean(search);
      // const shouldUseCoords = hasCoords && !hasOrgFilter && !hasSearchFilter;

      const params = {
        limit: pagination.limit,
        offset,
        ...(hasOrgFilter && {
          orgId: Number(orgId),
          onlyAssign: Boolean(orgId),
        }),
        ...(hasSearchFilter && {
          search,
        }),
        search,
        status: selectedStatus,
        // ...(shouldUseCoords && {
        //   current_lat: coords.lat,
        //   current_lng: coords.lng,
        //   search_radius: coords.radius,
        // }),
      };

      const res = await getLocationOrgs(params);
      const { result, total: resTotal, status } = res ?? {};

      if (!status || !Array.isArray(result)) {
        setLocations([]);
        setTotal(0);
        return;
      }

      setLocations(result.map(parseLocationItem).filter(Boolean));
      setTotal(Number(resTotal) || 0);
    } catch (err) {
      console.error("Fetch locations error:", err);
      setLocations([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    // hasCoords,
    pagination.limit,
    offset,
    orgId,
    search,
    // coords,
    selectedStatus,
  ]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { locations, total, isLoading };
};

const AssignTag = ({ orgInfo }) => (
  <span
    style={{
      backgroundColor: orgInfo.colorBgPrimary,
      color: orgInfo.colorTextPrimary,
      fontSize: 11,
      fontWeight: 600,
      height: 22,
      display: "inline-flex",
      alignItems: "center",
      borderRadius: 20,
      padding: "0 10px",
    }}
  >
    {orgInfo.orgShortCode}
  </span>
);

const LocationCard = ({ item, onSelect }) => {
  const { t } = useTranslation();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect({ lat: item.lat, lng: item.lng })}
      onKeyDown={(e) =>
        e.key === "Enter" && onSelect({ lat: item.lat, lng: item.lng })
      }
      className="location-card d-flex flex-column"
      style={{
        padding: "14px 16px",
        marginBottom: 8,
        border: "1px solid #f0f0f0",
        borderRadius: 10,
        cursor: "pointer",
        backgroundColor: "#fff",
        transition: "box-shadow 0.2s",
        outline: "none",
        gap: 12,
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.08)")
      }
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
      onFocus={(e) =>
        (e.currentTarget.style.boxShadow = "0 0 0 2px rgba(13,110,253,0.3)")
      }
      onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
    >
      <div className="d-flex justify-content-between align-items-center">
        {item.name}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",

            minWidth: "98px",
            height: "26px",
            padding: "0 10px",
            borderRadius: "6px",

            backgroundColor: item.status
              ? "var(--color-functional-foreground-success-subtle)"
              : "var(--color-functional-foreground-error-subtle)",
            color: item.status
              ? "var(--color-functional-content-success)"
              : "var(--color-functional-content-error)",

            fontSize: "12px",
            fontWeight: 500,
            boxSizing: "border-box",
          }}
        >
          {item.status
            ? t("hrms_setting_location_active_status")
            : t("hrms_setting_location_inactive_status")}
        </span>
      </div>

      <div
        className="d-flex flex-column"
        style={{ fontSize: 12, color: "#888" }}
      >
        <span>
          {t("hrms_setting_location_form_title_en")}: {item.nameEn}
        </span>
        <span>
          {t("hrms_setting_location_form_title_th")}: {item.nameTh}
        </span>
      </div>

      <div
        className="d-flex flex-wrap gap-3"
        style={{ fontSize: 12, color: "#888" }}
      >
        <span>
          {t("hrms_setting_location_form_lat")}: {item.lat}
        </span>
        <span>
          {t("hrms_setting_location_form_long")}: {item.lng}
        </span>
        <span>
          {t("hrms_setting_location_form_radius")}: {formatNumber(item.radius)}
        </span>
      </div>

      <div className="d-flex flex-wrap gap-2 align-items-center">
        <span style={{ fontSize: 11, color: "#aaa" }}>
          {t("hrms_setting_location_assign_to")}
        </span>
        {item.assignTo?.length > 0 ? (
          item.assignTo.map((a, i) => <AssignTag key={i} orgInfo={a.orgInfo} />)
        ) : (
          <span style={{ fontSize: 11, color: "#ccc" }}>-</span>
        )}
      </div>
    </div>
  );
};
const LocationListHeader = ({
  control,
  orgRole,
  // errors,
  onFieldChange,
  // onClickPin,
  onClear,
  showClear,
  // clearFieldErrors,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="bg-white"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1,
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      <Row className="p-3 g-2 align-items-center">
        <Col xs={12}>
          <Controller
            name="search"
            control={control}
            render={({ field }) => (
              <InputSearch
                value={field.value}
                onChange={(val) => onFieldChange(val, field)}
                placeholder={t("hrms_setting_location_search")}
              />
            )}
          />
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <Controller
            name="selectedOrg"
            control={control}
            render={({ field }) => (
              <AutocompleteCustomA
                isClearable={true}
                value={field.value}
                options={[
                  ...orgRole.map((org) => ({
                    label: org.org,
                    value: org.org_id,
                  })),
                ]}
                placeholder={t("hrms_setting_location_filter_org")}
                onChange={(option) => {
                  onFieldChange(option, field);
                }}
              />
            )}
          />
        </Col>
        {/* <Col xs={12} sm={4}>
          <Controller
            name="lat"
            control={control}
            render={({ field }) => (
              <InputSearch
                value={field.value}
                onChange={(val) => {
                  field.onChange(val);
                  clearFieldErrors("lat");
                }}
                placeholder={t("hrms_setting_location_form_lat_placeholder")}
                hideIcon={true}
                error={!!errors?.lat}
              />
            )}
          />
        </Col> */}
        {/* <Col xs={12} sm={4}>
          <Controller
            name="lng"
            control={control}
            render={({ field }) => (
              <InputSearch
                value={field.value}
                onChange={(val) => {
                  field.onChange(val);
                  clearFieldErrors("lng");
                }}
                placeholder={t("hrms_setting_location_form_long_placeholder")}
                hideIcon={true}
                error={!!errors?.lng}
              />
            )}
          />
        </Col> */}
        {/* <Col xs={12} sm={4}>
          <Button
            type="button"
            onClick={onClickPin}
            style={{
              ...getLocationButtonStyle(),
              flexShrink: 0,
              height: "40px",
            }}
          >
            <TbPinned size={18} />
            <span style={buttonTextStyle}>
              {t("hrms_setting_location_form_pin_bt")}
            </span>
          </Button>
        </Col> */}
        <Col xs={12} sm={6} lg={4}>
          <Controller
            name="selectedStatus"
            control={control}
            render={({ field }) => (
              <AutocompleteCustomA
                isClearable={true}
                value={field.value}
                options={[
                  {
                    label: t("hrms_setting_location_active_status"),
                    value: true,
                  },
                  {
                    label: t("hrms_setting_location_inactive_status"),
                    value: false,
                  },
                ]}
                onChange={(option) => {
                  onFieldChange(option, field);
                }}
                placeholder={t("hrms_setting_location_filter_status")}
              />
            )}
          />
        </Col>
        {showClear && (
          <Col xs={12} lg={4}>
            <Button
              type="button"
              onClick={onClear}
              style={{
                ...getLocationClearButtonStyle(),
                flexShrink: 0,
                height: "40px",
                width: "100%",
              }}
            >
              <span style={buttonTextStyle}>
                {t("hrms_setting_location_clear_bt")}
              </span>
            </Button>
          </Col>
        )}
      </Row>
    </div>
  );
};

const PaginationBar = ({ page, limit, total, onPageChange, onLimitChange }) => {
  const { t } = useTranslation();
  const totalPages = Math.ceil(total / limit);

  const pageItems = useMemo(() => {
    if (totalPages <= 0) return [];

    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);
    const items = [];

    const makeItem = (num) => (
      <BsPagination.Item
        key={num}
        active={page === num}
        onClick={() => onPageChange(num)}
      >
        {num}
      </BsPagination.Item>
    );

    items.push(makeItem(1));
    if (left > 2)
      items.push(<BsPagination.Ellipsis key="left-ellipsis" disabled />);
    for (let i = left; i <= right; i++) items.push(makeItem(i));
    if (right < totalPages - 1)
      items.push(<BsPagination.Ellipsis key="right-ellipsis" disabled />);
    if (totalPages > 1) items.push(makeItem(totalPages));

    return items;
  }, [page, totalPages, onPageChange]);

  return (
    <div
      className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3"
      style={{ borderTop: "1px solid #ddd" }}
    >
      <div className="d-flex align-items-center gap-2">
        <span
          className="text-nowrap"
          style={{ color: "var(--color-on-base-content-low)", fontSize: 13 }}
        >
          {t("hrms_setting_location_row_page")}
        </span>
        <Form.Select
          size="sm"
          value={limit}
          onChange={(e) => onLimitChange(Number(e.target.value))}
          style={{
            width: 75,
            color: "var(--color-on-base-content-low)",
            border: "none",
            boxShadow: "none",
            outline: "none",
          }}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Form.Select>
      </div>

      <Pagination
        size="small"
        current={page}
        pageSize={limit}
        total={totalPages * limit}
        showSizeChanger={false}
        onChange={(p) => {
          onPageChange(p);
        }}
      />
    </div>
  );
};

const EmptyState = () => (
  <div
    className="d-flex flex-column align-items-center justify-content-center text-muted h-100"
    style={{ padding: "48px 16px" }}
  >
    <span style={{ fontSize: 14 }}>No location data found</span>
  </div>
);

const LocationPOI = () => {
  const { orgRole } = useOutletContext();

  const {
    control,
    reset,
    getValues,
    // setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      selectedOrg: null,
      search: "",
      lat: "",
      lng: "",
      selectedStatus: null,
    },
  });

  const { search, selectedOrg, selectedStatus } = getValues();
  // const [coords, setCoords] = useState({ lat: "", lng: "", radius: 0 });
  // const [mapPin, setMapPin] = useState(null);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [resetMap, setResetMap] = useState(false);

  // useEffect(() => {
  //   if (coords.lat && coords.lng) {
  //     setPagination((prev) => ({ ...prev, page: 1 }));
  //   }
  // }, [coords]);

  const { locations, total, isLoading } = useLocationData({
    orgId: selectedOrg && Number(selectedOrg?.value),
    search,
    pagination,
    // coords,
    selectedStatus: selectedStatus && Boolean(selectedStatus?.value),
  });

  const resetPage = useCallback(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleFieldChange = useCallback(
    (val, field) => {
      field.onChange(val);
      resetPage();
    },
    [resetPage],
  );

  // const handleGetLocation = () => {
  //   const { lat, lng } = getValues();
  //   let hasError = false;

  //   if (!lat) {
  //     setError("lat", { type: "manual", message: "required" });
  //     hasError = true;
  //   } else if (!LAT_REGEX.test(lat)) {
  //     setError("lat", { type: "manual", message: "invalid" });
  //     hasError = true;
  //   } else {
  //     clearErrors("lat");
  //   }

  //   if (!lng) {
  //     setError("lng", { type: "manual", message: "required" });
  //     hasError = true;
  //   } else if (!LNG_REGEX.test(lng)) {
  //     setError("lng", { type: "manual", message: "invalid" });
  //     hasError = true;
  //   } else {
  //     clearErrors("lng");
  //   }

  //   if (hasError) {
  //     setMapPin(null);
  //     return;
  //   }

  //   setMapPin({ lat: Number(lat), long: Number(lng) });
  // };

  const handleClear = useCallback(() => {
    reset();
    setSelectedPosition(null);
    // setMapPin(null);
    // setCoords({ lat: "", lng: "", radius: 0 });
    clearErrors();
    resetPage();
    setResetMap((prev) => !prev);
  }, [reset, resetPage, clearErrors, setSelectedPosition]);

  return (
    <div className="p-3">
      <Row className="g-3" style={{ minHeight: "calc(100vh - 170px)" }}>
        {/* Map */}
        <Col xs={12} lg={7} xl={8}>
          <div
            style={{
              height: "100%",
              minHeight: "400px",
              borderRadius: 8,
              overflow: "hidden",
              border: "1px solid #ddd",
            }}
          >
            <GoogleMultiple
              markerArray={locations}
              selectedPosition={selectedPosition}
              // setSelectedPosition={setSelectedPosition}
              // markPin={mapPin}
              resetMap={resetMap}
            />
          </div>
        </Col>

        {/* Location List */}
        <Col xs={12} lg={5} xl={4}>
          <div
            className="d-flex flex-column"
            style={{
              height: "clamp(800px, 100vw, calc(100vh - 170px))",
              border: "1px solid #ddd",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <LocationListHeader
              control={control}
              orgRole={orgRole}
              onFieldChange={handleFieldChange}
              // onClickPin={handleGetLocation}
              errors={errors}
              onClear={handleClear}
              clearFieldErrors={clearErrors}
              showClear={
                selectedOrg || search || selectedStatus || selectedPosition
              }
            />

            <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
              {isLoading ? (
                <Loading />
              ) : locations.length === 0 ? (
                <EmptyState />
              ) : (
                locations.map((item, index) => (
                  <LocationCard
                    key={index}
                    item={item}
                    onSelect={setSelectedPosition}
                  />
                ))
              )}
            </div>

            <PaginationBar
              page={pagination.page}
              limit={pagination.limit}
              total={total}
              onPageChange={(page) =>
                setPagination((prev) => ({ ...prev, page }))
              }
              onLimitChange={(limit) =>
                setPagination({ ...DEFAULT_PAGINATION, limit })
              }
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default LocationPOI;
